/**
 * ReportUserSheetContent
 *
 * UI component for the report user sheet.
 * Owns the stepper ref and imperative navigation calls.
 * All state and use-case execution lives in useReportUser.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { ExpauseStepper, type ExpauseStepperRef } from '@/presentation/components/common/stepper/ExpauseStepper';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { useReportUser } from '../hooks/useReportUser';
import { ReportItemsStep } from './steps/ReportItemsStep';
import { ReportSubmitStep } from './steps/ReportSubmitStep';
import { ReportUserTopBar } from './ReportUserTopBar';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportUserSheetContentProps {
  user: UserEntity;
  onFinish: () => void;
}

export const ReportUserSheetContent: React.FC<ReportUserSheetContentProps> = ({
  user,
  onFinish,
}) => {
  // stepperRef drives imperative navigation (goNext / goPrevious).
  // currentStep is kept in sync via onStepChanged to gate the back button.
  const stepperRef = useRef<ExpauseStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const {
    levels,
    submitItem,
    comment,
    isSubmitting,
    isLastStep,
    pendingNavigation,
    clearPendingNavigation,
    setComment,
    handleItemTap,
    handleBack: onBack,
    trimLastLevel,
    handleSubmit,
  } = useReportUser({ user, onFinish });

  // useReportUser signals navigation via pendingNavigation rather than calling
  // stepperRef directly — keeping ref/DOM concerns out of the hook.
  // We watch the flag here and drive the stepper imperatively.
  useEffect(() => {
    if (pendingNavigation) {
      stepperRef.current?.goNext();
      clearPendingNavigation();
    }
  }, [pendingNavigation]);

  // State mutations (clearing submitItem) happen in the hook immediately.
  // Level trimming is deferred until after the animation completes — trimming while
  // the slide is in flight shifts the submit step into the viewport mid-transition.
  // For fade this is also safe: the invisible node simply lingers a few extra ms.
  const handleBack = () => {
    if (currentStep === 0) return;
    onBack();
    stepperRef.current?.goPrevious();
    if (!isLastStep) {
      setTimeout(trimLastLevel, ANIMATION_CONSTANTS.duration.normal + 50);
    }
  };

  // Steps are built dynamically from levels.
  //
  // Each level entry is either a populated array (items ready) or null (still fetching).
  // A null entry is pushed into levels before navigation so the step already exists
  // when the stepper moves to it — the loading indicator shows on arrival rather than
  // blocking the tap.
  //
  // The submit step is always appended as the final entry. The stepper never reaches
  // it via index — navigation is driven explicitly by handleItemTap when isSubmit===true.
  const steps = useMemo(() => {
    const levelSteps = levels.map((items, index) => ({
      id: `level-${index}`, // stable per drill-down depth; prevents DOM node reuse on trim
      content: (
        <ReportItemsStep
          items={items ?? []}
          isLoading={items === null}  // null === placeholder, fetch still in flight
          onItemTap={handleItemTap}
        />
      ),
    }));

    const submitStep = {
      id: 'submit', // always the same node regardless of how many levels are open
      content: (
        <ReportSubmitStep
          submitItem={submitItem}
          comment={comment}
          isSubmitting={isSubmitting}
          onCommentChange={setComment}
          onSubmit={handleSubmit}
        />
      ),
    };

    return [...levelSteps, submitStep];
  }, [levels, submitItem, comment, isSubmitting, handleItemTap, handleSubmit, setComment]);

  return (
    <div
      {...qaAttr({
        id: 'report-user-sheet',
        status: isSubmitting ? 'submitting' : 'selecting',
      })}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <ReportUserTopBar
        showBack={currentStep > 0}
        isLastStep={isLastStep}
        onBack={handleBack}
        // Disable close during submission to prevent accidental loss of progress
        onClose={() => isSubmitting ? undefined : onFinish()}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <ExpauseStepper
          ref={stepperRef}
          progressProps={{ showProgress: false }}
          initialStep={0}
          animationType="slide"
          onStepChanged={setCurrentStep}
          steps={steps}
        />
      </div>
    </div>
  );
};
